// Wie ein Brocken aussieht: welches Adernbild sein Erz traegt, wie hell es
// leuchtet und wie ein reicher Brocken sich vom gewoehnlichen unterscheidet
// (VIEW-001, Gates FG-40, FG-42).
//
// Es gab drei Fassungen. Die erste malte jeden Brocken pro Frame als Polygon.
// Die zweite buk sechzehn Bilder, die sich nicht drehten. Die dritte rechnete
// jeden Brocken im Shader aus **einer** Gesteinsoberflaeche — einzigartig, aber
// ein Shader-Objekt beendet in Phaser den laufenden Stapel: gemessen **41 gegen
// 95 Bilder je Sekunde**, ein Draw-Call je sichtbarem Brocken.
//
// Heute sind es wieder Bilder, aber achtundzwanzig davon in **einem** Raster,
// frei gedreht und aus der Brocken-ID gespiegelt. Die Erzadern liegen als
// zweites Raster additiv darueber. Zwei Texturen, zwei Draw-Calls, unabhaengig
// von der Brockenzahl.
//
// Diese Datei importiert **kein Phaser**: sie ist reine Rechnung, damit ein Test
// sie lesen kann. Wer die Sprites an die Szene haengt, steht in `scenes/`.

import { VEIN_VARIANTS } from './sprites';

/** Was diese Datei aus einer Erzzeile braucht — nicht mehr. */
export interface VeinSource {
  id: string;
  /** Dichte der Zeichnung: klein heisst grob und sparsam, gross fein und verzweigt. */
  veinScale: number;
  /** Leuchtstaerke der Adern. */
  veinGlow: number;
}

/**
 * Wie viel heller die Adern eines reichen Brockens leuchten.
 *
 * Derselbe Faktor, mit dem der Shader sie angehoben hat. Der Koerper geht
 * gleichzeitig ins Dunkle (`ROCK_RICH_TINT` in `palette.ts`) — beides zusammen
 * ist der Unterschied, den frueher ein zweiter Satz Bilder getragen hat.
 */
export const RICH_VEIN_BOOST = 1.7;

/**
 * Wie stark die Adernebene insgesamt aufliegt — gemessen im Spiel, nicht geraten.
 *
 * `vein_glow` in der Tabelle ist ein **relativer** Wert: er sagt, welches Erz
 * heller leuchtet als welches. Wie hell die Ebene absolut wird, hängt daran, wie
 * das Bild aussieht — und ein additiv aufgelegtes Weiss auf schwarzem Grund wird
 * mit 0,3 bereits gleissend. Die erste Fassung nahm `vein_glow` direkt: die
 * Brocken sahen aus wie weisse Schneeflocken, das Gestein darunter war weg.
 *
 * Die Kalibrierung gehört hierher und nicht in die Tabelle: dort stünde sie in
 * zwölf Zeilen, und wer das Adernbild austauscht, müsste alle zwölf anfassen.
 */
export const VEIN_OPACITY = 0.7;

/**
 * Welches Adernbild ein Erz bekommt — aus seinem **Rang** in der Dichte.
 *
 * Nicht aus einem neuen Tabellenfeld: `veinScale` steht bereits neben der Farbe,
 * die es verstaerkt, und ein zweites Feld daneben waere die Kopie, die still
 * veraltet (FG-31). Der Rang statt des Rohwerts, weil die Bilder eine feste
 * Reihenfolge von grob nach fein sind und die Dichten der Tabelle keine
 * Skala teilen — er bildet zwoelf Erze auf zwoelf Bilder ab und bleibt gueltig,
 * wenn eine Aera Erze ergaenzt.
 *
 * Die Ordnung braucht einen festgelegten Tie-Breaker: bei gleicher Dichte
 * entschiede sonst die Ladereihenfolge, und zwei Erze tauschten ihr Bild, sobald
 * jemand eine Tabellenzeile verschiebt.
 */
export function veinFrameOf(ore: VeinSource, alle: readonly VeinSource[]): number {
  const rang = alle.filter(
    (other) => other.veinScale < ore.veinScale
      || (other.veinScale === ore.veinScale && other.id < ore.id),
  ).length;
  const spanne = Math.max(1, alle.length);
  return Math.min(VEIN_VARIANTS - 1, Math.floor((rang * VEIN_VARIANTS) / spanne));
}

/**
 * Deckkraft der Adernebene: Leuchtstaerke aus der Tabelle mal Kalibrierung.
 *
 * Geklemmt, weil ein reicher Brocken sie anhebt und Phaser ueber 1 nichts mehr
 * tut — ein ungeklemmter Wert waere ein stiller Deckel statt einer Zahl.
 */
export function veinAlpha(ore: VeinSource, rich: boolean): number {
  const roh = ore.veinGlow * VEIN_OPACITY * (rich ? RICH_VEIN_BOOST : 1);
  return Math.max(0, Math.min(1, roh));
}
